import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { taskApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { Btn, Spinner, EmptyState } from '../components/ui';
import TaskCard from '../components/TaskCard';

const STATUSES   = ['ALL','PENDING','IN_PROGRESS','ON_HOLD','DONE','OVERDUE'];
const PRIORITIES = ['ALL','LOW','MEDIUM','HIGH','CRITICAL'];
const STATUS_LABEL = { ALL: 'All', PENDING: 'Pending', IN_PROGRESS: 'In Progress', ON_HOLD: 'On Hold', DONE: 'Done', OVERDUE: 'Overdue' };

function Chip({ active, onClick, children, danger }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '6px 14px', borderRadius: '999px', cursor: 'pointer',
      fontSize: '13px', fontWeight: active ? 700 : 400,
      background: active ? (danger ? '#EF4444' : '#2563EB') : '#fff',
      color:      active ? '#fff' : '#6B7280',
      border:     `1.5px solid ${active ? (danger ? '#EF4444' : '#2563EB') : '#E5E7EB'}`,
      transition: 'all 0.12s',
      whiteSpace: 'nowrap',
    }}>{children}</button>
  );
}

export default function Tasks() {
  const { user }                = useAuth();
  const navigate                = useNavigate();
  const location                = useLocation();
  const [tasks,    setTasks]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [flash,    setFlash]    = useState(location.state?.flash || '');
  const [search,   setSearch]   = useState('');
  const [status,   setStatus]   = useState('ALL');
  const [priority, setPriority] = useState('ALL');

  const isEmployee = user?.role === 'EMPLOYEE';

  useEffect(() => {
    if (location.state?.flash) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(''), 6000);
    return () => clearTimeout(t);
  }, [flash]);

  const load = useCallback(() => {
    setLoading(true);
    const fn = isEmployee ? taskApi.getMyTasks : taskApi.getAll;
    fn()
      .then(r => setTasks(r.data.data || []))
      .catch(() => setError('Failed to load tasks'))
      .finally(() => setLoading(false));
  }, [isEmployee]);

  useEffect(() => { load(); }, [load]);

  const filtered = tasks.filter(t => {
    const ms = !search   || t.title?.toLowerCase().includes(search.toLowerCase())
                         || t.assignedTo?.name?.toLowerCase().includes(search.toLowerCase());
    const mv = status   === 'ALL' || t.status   === status;
    const mp = priority === 'ALL' || t.priority === priority;
    return ms && mv && mp;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (a.status === 'OVERDUE' && b.status !== 'OVERDUE') return -1;
    if (b.status === 'OVERDUE' && a.status !== 'OVERDUE') return  1;
    return new Date(a.deadline) - new Date(b.deadline);
  });

  const overdueCount  = tasks.filter(t => t.status === 'OVERDUE').length;
  const criticalCount = tasks.filter(t => t.priority === 'CRITICAL').length;

  return (
    <div style={{ padding: '20px 16px', maxWidth: '800px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 800, color: '#111827', letterSpacing: '-0.04em', fontFamily: "'Sora', sans-serif" }}>
            {isEmployee ? 'My Tasks' : 'All Tasks'}
          </h1>
          <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '4px' }}>
            {tasks.length} tasks
            {overdueCount > 0 && <span style={{ color: '#EF4444', fontWeight: 700 }}> · {overdueCount} overdue</span>}
            {criticalCount > 0 && <span style={{ color: '#DC2626', fontWeight: 700 }}> · {criticalCount} critical</span>}
          </p>
        </div>
        {!isEmployee && (
          <Btn onClick={() => navigate('/tasks/create')} size="sm" icon="+" style={{ flexShrink: 0 }}>
            New Task
          </Btn>
        )}
      </div>

      {flash && (
        <div style={{
          padding: '12px 16px', background: '#ECFDF5', border: '1px solid #A7F3D0',
          borderRadius: '12px', color: '#065F46', fontSize: '14px', marginBottom: '16px',
        }}>
          {flash}
        </div>
      )}

      {/* Away banner */}
      {isEmployee && user?.away && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '12px', marginBottom: '16px', fontSize: '13px', color: '#92400E' }}>
          <span style={{ fontSize: '18px' }}>🏖</span>
          <span>You're marked as Away{user.awayReturnDate ? ` · returning ${user.awayReturnDate}` : ''}. Tasks are still visible to your Admin.</span>
        </div>
      )}

      {/* Overdue alert banner */}
      {overdueCount > 0 && status === 'ALL' && (
        <div
          onClick={() => setStatus('OVERDUE')}
          style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '12px', marginBottom: '16px', cursor: 'pointer' }}
        >
          <span style={{ fontSize: '20px' }}>🚨</span>
          <div style={{ flex: 1 }}>
            <p style={{ fontWeight: 700, color: '#DC2626', fontSize: '14px' }}>{overdueCount} task{overdueCount > 1 ? 's are' : ' is'} overdue</p>
            <p style={{ fontSize: '12px', color: '#EF4444', marginTop: '2px' }}>Tap to filter overdue tasks</p>
          </div>
          <span style={{ color: '#EF4444', fontSize: '18px' }}>→</span>
        </div>
      )}

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '14px' }}>
        <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '15px', color: '#9CA3AF', pointerEvents: 'none' }}>🔍</span>
        <input
          placeholder={isEmployee ? 'Search my tasks…' : 'Search tasks or employee…'}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            width: '100%', padding: '11px 14px 11px 42px',
            borderRadius: '12px', border: '1.5px solid #E5E7EB',
            background: '#F9FAFB', fontSize: '14px', color: '#111827', outline: 'none',
          }}
          onFocus={e => e.target.style.borderColor = '#2563EB'}
          onBlur={e  => e.target.style.borderColor = '#E5E7EB'}
        />
        {search && (
          <button onClick={() => setSearch('')} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#9CA3AF', padding: '2px' }}>×</button>
        )}
      </div>

      {/* Status filter */}
      <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '4px', marginBottom: '8px', scrollbarWidth: 'none' }}>
        {STATUSES.map(s => (
          <Chip key={s} active={status === s} onClick={() => setStatus(s)} danger={s === 'OVERDUE'}>
            {STATUS_LABEL[s]}
            {s === 'OVERDUE' && overdueCount > 0 && (
              <span style={{ marginLeft: '2px', background: status === 'OVERDUE' ? 'rgba(255,255,255,0.3)' : '#EF4444', color: '#fff', borderRadius: '999px', fontSize: '10px', padding: '1px 5px', fontWeight: 700 }}>{overdueCount}</span>
            )}
          </Chip>
        ))}
      </div>

      {/* Priority filter */}
      <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '8px', marginBottom: '16px', scrollbarWidth: 'none' }}>
        {PRIORITIES.map(p => (
          <Chip key={p} active={priority === p} onClick={() => setPriority(p)}>
            {p === 'ALL' ? 'Any Priority' : p.charAt(0) + p.slice(1).toLowerCase()}
          </Chip>
        ))}
      </div>

      {/* Results info */}
      {(search || status !== 'ALL' || priority !== 'ALL') && !loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <p style={{ fontSize: '13px', color: '#6B7280' }}>
            {sorted.length} result{sorted.length !== 1 ? 's' : ''}
          </p>
          <button onClick={() => { setSearch(''); setStatus('ALL'); setPriority('ALL'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#2563EB', fontWeight: 600 }}>
            Clear filters
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ padding: '12px 16px', background: '#FEF2F2', borderRadius: '12px', color: '#DC2626', fontSize: '14px', marginBottom: '16px', border: '1px solid #FECACA' }}>⚠ {error}</div>
      )}

      {/* Task list */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[1,2,3,4].map(i => (
            <div key={i} className="skeleton" style={{ height: '88px', borderRadius: '14px' }} />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <EmptyState
          icon="✓"
          title={search ? 'No tasks match' : status !== 'ALL' ? `No ${STATUS_LABEL[status].toLowerCase()} tasks` : 'No tasks yet'}
          subtitle={
            !search && isEmployee
              ? 'Your Admin will assign tasks here.'
              : !search && !isEmployee
              ? 'Create a task to get started.'
              : 'Try adjusting your filters.'
          }
          action={!isEmployee && !search && status === 'ALL' && (
            <Btn onClick={() => navigate('/tasks/create')}>Create First Task</Btn>
          )}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {sorted.map(task => <TaskCard key={task.id} task={task} />)}
        </div>
      )}
    </div>
  );
}
